import { ActionType, RegionId } from "@sp-crm/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { ReferralTierEditor } from "components/manage/agency-settings/referral-tier-editor";
import { trpc } from "components/rpc/trpc";
import { Checkbox } from "components/ui/checkbox";
import { ErrorMessage } from "components/ui/error-message";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Spinner } from "components/ui/spinner";
import { Toggle } from "components/ui/toggle";
import React, { ChangeEvent, useCallback } from "react";
import {
    useCurrentRegion,
    useIsAllowedInWholeTenant,
    useRegionId,
} from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { AgencySettingsLink } from "./agency-settings-link";
import { RegionSettingsReferralPipelines } from "./region-settings-referral-pipelines";

export const RegionSettingsReferralTiers: React.FC<unknown> = () => {
    const region = useCurrentRegion();
    const regionId = useRegionId();
    const canManageAgencySettings = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();

    const stateQuery = useQuery({
        ...trpcClient.referralTier.officeTierState.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: !!regionId,
    });

    const onScopeChange = () => {
        queryClient.invalidateQueries({ queryKey: [["referralTier"]] });
        queryClient.invalidateQueries({ queryKey: [["marketingPlaybook"]] });
        queryClient.invalidateQueries({ queryKey: ["GetRegionSettings"] });
    };

    const customize = useMutation({
        ...trpcClient.referralTier.customizeOfficeTiers.mutationOptions(),
        onSuccess: onScopeChange,
    });
    const revert = useMutation({
        ...trpcClient.referralTier.revertOfficeTiers.mutationOptions(),
        onSuccess: onScopeChange,
    });
    const enable = useMutation({
        ...trpcClient.referralTier.enableOfficeTiers.mutationOptions(),
        onSuccess: onScopeChange,
    });
    const disable = useMutation({
        ...trpcClient.referralTier.disableOfficeTiers.mutationOptions(),
        onSuccess: onScopeChange,
    });

    const overridden = stateQuery.data?.overridden ?? false;
    const customizable = stateQuery.data?.customizable ?? false;
    const tiersEnabled = stateQuery.data?.tiersEnabled ?? false;
    const tenantTiersEnabled = stateQuery.data?.tenantTiersEnabled ?? false;
    const sharedPool = stateQuery.data?.sharedPool ?? false;
    const pending =
        customize.isPending || revert.isPending || enable.isPending || disable.isPending;
    const mutationFailed =
        customize.isError || revert.isError || enable.isError || disable.isError;

    const handleOverrideChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            if (!regionId) {
                return;
            }
            if (e.target.checked) {
                handleEvent("referral-tier-office-customize");
                customize.mutate({ regionId });
            } else {
                const confirmed = await fancyConfirm(
                    "Use agency settings?",
                    `This will hide the referral tier settings specific to ${region?.name} and use the agency settings instead. The office tiers and their touchpoints are kept and come back if you switch back later.`,
                    "Yes, use agency settings",
                    "No, cancel",
                    { confirmStyle: "question" },
                );
                if (confirmed) {
                    handleEvent("referral-tier-office-revert");
                    revert.mutate({ regionId });
                }
            }
        },
        [customize, revert, regionId, region],
    );

    const handleEnabledChange = useCallback(
        (isChecked: boolean) => {
            if (!regionId) {
                return;
            }
            if (isChecked) {
                enable.mutate({ regionId });
            } else {
                disable.mutate({ regionId });
            }
        },
        [enable, disable, regionId],
    );

    if (!regionId) {
        return null;
    }
    if (stateQuery.isPending) {
        return <Spinner />;
    }
    if (stateQuery.isError || !stateQuery.data) {
        return (
            <ErrorMessage component="RegionSettingsReferralTiers">
                <pre>{JSON.stringify(stateQuery.error, null, 2)}</pre>
            </ErrorMessage>
        );
    }

    const effectiveEnabled = overridden ? tiersEnabled : tenantTiersEnabled;
    const canEditOffice = overridden && customizable;
    const showEditor = effectiveEnabled && (canEditOffice || !overridden);

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>{`Referrals - ${region?.name}`}</ContentHeader>
                <ContentSubheader>
                    Rate referral sources and communities with a tier scale.
                </ContentSubheader>
                <div className="space-y-4">
                    {customizable || overridden ? (
                        <Checkbox
                            checked={overridden}
                            disabled={pending}
                            onChange={handleOverrideChange}
                            label={
                                <p>
                                    Use specific referral tier settings for{" "}
                                    <strong>{region?.name}</strong>
                                </p>
                            }
                        />
                    ) : null}
                    {mutationFailed ? (
                        <InlineBanner type="error">
                            The change could not be saved. Please try again.
                        </InlineBanner>
                    ) : null}
                    {!overridden ? (
                        <InlineBanner type="info">
                            {sharedPool && !customizable
                                ? `${region?.name} shares referral sources with other offices, so the settings below apply to the whole agency and are managed in the `
                                : "The settings below apply to the whole agency and are managed in the "}
                            <AgencySettingsLink
                                canManageAgencySettings={canManageAgencySettings}
                            />
                            {customizable
                                ? ". Check the box above to customize them for this office."
                                : "."}
                        </InlineBanner>
                    ) : null}
                    {overridden && !customizable ? (
                        <InlineBanner type="info">
                            {`${region?.name} now shares referral sources with other offices, so its referral tier settings can no longer be edited here. The office settings stay in effect until you switch back to the agency settings.`}
                        </InlineBanner>
                    ) : null}
                    <Toggle
                        checked={effectiveEnabled}
                        disabled={!canEditOffice || pending}
                        onChange={handleEnabledChange}
                        label={
                            <div>
                                <span className="text-md">
                                    Enable referral source tiers
                                </span>
                                <div className="text-gray-500 text-sm">
                                    {
                                        "Contacts inherit their organization's tier unless overridden."
                                    }
                                </div>
                            </div>
                        }
                    />
                    {showEditor ? (
                        <ReferralTierEditor
                            scope={
                                overridden
                                    ? { kind: "office", regionId }
                                    : { kind: "agencyWide" }
                            }
                            disabled={!overridden}
                        />
                    ) : null}
                </div>
            </ContentFull>
            <RegionSettingsReferralPipelines />
        </div>
    );
};
